import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Activity, Compass, Users, Handshake, MessageSquare, BookmarkCheck,
  Megaphone, FileText, ShieldCheck, Shield, Download, Trash2, Database, Eye,
  Lock, Cpu, User as UserIcon, Loader2, AlertTriangle, Clock,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { TwoFactorAuth } from '../components/brand/TwoFactorAuth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getNav = (role) => {
  if (role === 'clinician') {
    return [
      { icon: Activity, label: 'Dashboard', href: '/clinician/dashboard' },
      { icon: Compass, label: 'Practice Coach', href: '/clinician/coach', highlight: true },
      { icon: Handshake, label: 'Offers Inbox', href: '/clinician/offers' },
      { icon: Megaphone, label: 'Care Requests', href: '/clinician/care-requests' },
      { icon: FileText, label: 'Clinical Notes', href: '/clinician/notes' },
      { icon: MessageSquare, label: 'Messages', href: '/clinician/messages' },
      { icon: ShieldCheck, label: 'Privacy & Data', active: true },
    ];
  }
  return [
    { icon: Activity, label: 'Dashboard', href: '/patient/dashboard' },
    { icon: Compass, label: 'Care Navigator', href: '/patient/navigator', highlight: true },
    { icon: Users, label: 'Find Clinicians', href: '/patient/find-clinicians' },
    { icon: BookmarkCheck, label: 'Saved Searches', href: '/patient/saved-searches' },
    { icon: Megaphone, label: 'Post a Need', href: '/patient/care-requests' },
    { icon: Handshake, label: 'Offers & Bookings', href: '/patient/offers' },
    { icon: MessageSquare, label: 'Messages', href: '/patient/messages' },
    { icon: ShieldCheck, label: 'Privacy & Data', active: true },
  ];
};

const actorIcon = (role) => {
  if (role === 'system') return Cpu;
  if (role === 'clinician') return ShieldCheck;
  return UserIcon;
};

export default function PrivacyDashboard() {
  const { user, getAuthHeader, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [accessLog, setAccessLog] = useState([]);
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportJob, setExportJob] = useState(null);
  const [exportHistory, setExportHistory] = useState([]);
  const [deletingCat, setDeletingCat] = useState(null);
  const [confirmCat, setConfirmCat] = useState(null);
  const [accountDialog, setAccountDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ov, al, hist] = await Promise.all([
        axios.get(`${API}/privacy/overview`, getAuthHeader()),
        axios.get(`${API}/privacy/access-log`, getAuthHeader()),
        axios.get(`${API}/hipaa/export/my/history`, getAuthHeader()).catch(() => ({ data: { jobs: [] } })),
      ]);
      setOverview(ov.data);
      setAccessLog(al.data.access_log || []);
      const jobs = hist.data.jobs || [];
      setExportHistory(jobs);
      // Auto-track the most recent still-processing job so the UI polls it.
      const processing = jobs.find((j) => j.status === 'processing');
      if (processing) setExportJob(processing);
      axios.get(`${API}/consent`, getAuthHeader()).then((r) => setConsent(r.data)).catch(() => {});
    } catch (e) {
      toast.error('Could not load your privacy data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Poll the current export job while it's processing — HIPAA jobs are
  // usually ready within 1-2s but heavy accounts can take 20-30s.
  useEffect(() => {
    if (!exportJob || exportJob.status !== 'processing') return undefined;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/hipaa/export/${exportJob.id}`, getAuthHeader());
        setExportJob(data.job);
        if (data.job.status === 'ready') {
          toast.success('Your data export is ready to download');
          // Refresh history so the completed job shows up in the list.
          axios.get(`${API}/hipaa/export/my/history`, getAuthHeader())
            .then((r) => setExportHistory(r.data.jobs || []))
            .catch(() => {});
        }
      } catch (e) {
        // Job disappeared — stop polling.
      }
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [exportJob?.id, exportJob?.status, getAuthHeader]);

  const requestExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.post(
        `${API}/hipaa/export/request`,
        { reason: 'user-initiated privacy dashboard export' },
        getAuthHeader(),
      );
      setExportJob(data.job);
      if (data.created) {
        toast.info('Preparing your export — this usually takes a few seconds.');
      } else {
        toast.info('Resuming an in-flight export request.');
      }
    } catch (e) {
      toast.error('Could not request export');
    } finally {
      setExporting(false);
    }
  };

  const downloadArtifact = async (jobId) => {
    try {
      const resp = await axios.get(
        `${API}/hipaa/export/${jobId}/download`,
        { ...getAuthHeader(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `caraloom-hipaa-export-${jobId.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const deleteCategory = async (cat) => {
    setDeletingCat(cat.key);
    try {
      const { data } = await axios.delete(`${API}/privacy/data/${cat.key}`, getAuthHeader());
      toast.success(`Deleted ${data.deleted} ${cat.label.toLowerCase()} record${data.deleted !== 1 ? 's' : ''}`);
      setConfirmCat(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    } finally {
      setDeletingCat(null);
    }
  };

  const deleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/privacy/account`, { ...getAuthHeader(), data: { confirmation: confirmText } });
      toast.success('Your account and data have been permanently deleted');
      logout();
      navigate('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete account');
      setDeletingAccount(false);
    }
  };

  return (
    <DashboardShell
      navItems={getNav(user?.role)}
      title="Your privacy & data"
      subtitle="See exactly what we hold, who's had access, take a copy, or erase it — your data, your control."
      testId="privacy-dashboard"
    >
      {loading ? (
        <div className="grid sm:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <div key={i} className="cn-card p-6 h-32 animate-pulse bg-cn-bg/60" />)}</div>
      ) : (
        <div className="space-y-8">
          {/* Account + assurance */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="cn-card p-6 text-white border-transparent lg:col-span-2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 55%, #334155 100%)' }} data-testid="privacy-account-card">
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-cn-accent/20 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase tracking-wider">
                  <Lock className="w-4 h-4" /> HIPAA-ready data controls
                </div>
                <h3 className="font-display font-extrabold text-2xl mt-3">{overview?.account?.name || 'Your account'}</h3>
                <p className="text-white/85 text-sm mt-1">{overview?.account?.email}</p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                  <Badge className="bg-white/15 text-white border-0 capitalize">{overview?.account?.role}</Badge>
                  <Badge className="bg-white/15 text-white border-0">{overview?.total_records} data records</Badge>
                  {overview?.account?.created_at && (
                    <Badge className="bg-white/15 text-white border-0">Member since {new Date(overview.account.created_at).toLocaleDateString()}</Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-6 flex flex-col" data-testid="privacy-export-card">
              <div className="w-10 h-10 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center mb-3">
                <Download className="w-5 h-5" />
              </div>
              <h3 className="cn-display text-lg text-cn-text dark:text-white">Take your data with you</h3>
              <p className="text-xs text-cn-text-muted mt-1">
                A complete HIPAA §164.524 Right of Access export — every section, in open JSON. Prepared in the background and audit-logged.
              </p>

              {/* Current job (in-flight or just-completed) */}
              {exportJob && (
                <div className="mt-4 rounded-lg border border-cn-border/50 bg-cn-bg/50 p-3 text-xs" data-testid="export-current-job">
                  {exportJob.status === 'processing' && (
                    <div className="flex items-center gap-2 text-cn-text-muted">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Preparing your export — polling every 3s…
                    </div>
                  )}
                  {exportJob.status === 'ready' && (
                    <div className="space-y-2">
                      <div className="text-emerald-600 font-medium">
                        Ready · {(exportJob.row_count || 0).toLocaleString()} rows across {exportJob.section_count} sections
                      </div>
                      <div className="text-cn-text-muted">
                        {Math.round((exportJob.artifact_bytes || 0) / 1024)} KB · valid until {new Date(exportJob.expires_at).toLocaleDateString()}
                      </div>
                      <Button
                        onClick={() => downloadArtifact(exportJob.id)}
                        className="cn-btn cn-btn-primary w-full mt-2 !py-2"
                        data-testid="download-current-export-btn"
                      >
                        <Download className="w-4 h-4" /> Download JSON
                      </Button>
                    </div>
                  )}
                  {exportJob.status === 'failed' && (
                    <div className="text-red-600">
                      Export failed — {exportJob.error || 'unknown error'}. Try again below.
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={requestExport}
                disabled={exporting || (exportJob && exportJob.status === 'processing')}
                className="cn-btn cn-btn-primary w-full mt-4 !py-2.5"
                data-testid="export-data-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {' '}Request new export
              </Button>

              {/* Historical exports */}
              {exportHistory.filter((j) => j.id !== exportJob?.id).length > 0 && (
                <div className="mt-4 pt-4 border-t border-cn-border/40" data-testid="export-history">
                  <div className="text-xs font-semibold text-cn-text mb-2">Previous exports</div>
                  <ul className="space-y-1.5 text-xs">
                    {exportHistory
                      .filter((j) => j.id !== exportJob?.id)
                      .slice(0, 5)
                      .map((j) => (
                        <li key={j.id} className="flex items-center justify-between gap-2">
                          <span className="text-cn-text-muted">
                            {new Date(j.created_at).toLocaleDateString()} · {j.row_count} rows
                          </span>
                          {j.status === 'ready' && j.download_available ? (
                            <button
                              type="button"
                              className="text-cn-primary hover:underline"
                              onClick={() => downloadArtifact(j.id)}
                              data-testid={`download-past-export-${j.id.slice(0, 8)}`}
                            >
                              download
                            </button>
                          ) : (
                            <span className="text-cn-text-muted italic">{j.status}</span>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* How your data is protected */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-cn-primary" />
              <h2 className="cn-display text-2xl text-cn-text dark:text-white">How your data is protected</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="privacy-protection">
              {[
                { icon: Lock, label: 'Encryption', value: 'TLS 1.2+ in transit · AES-256 at rest', tone: 'ok' },
                { icon: Eye, label: 'PHI access audit trail', value: `${accessLog.length} recorded access event${accessLog.length !== 1 ? 's' : ''}`, tone: 'ok' },
                {
                  icon: Cpu, label: 'AI processing',
                  value: overview?.ai_posture ? `${overview.ai_posture.provider} · ${overview.ai_posture.model}` : '—',
                  badge: overview?.ai_posture ? (overview.ai_posture.baa_signed ? 'BAA signed' : 'BAA pending') : null,
                  tone: overview?.ai_posture?.baa_signed ? 'ok' : 'warn',
                  sub: overview?.ai_posture?.zero_retention ? 'Zero-retention mode on' : null,
                },
                {
                  icon: ShieldCheck, label: 'Consent on file',
                  value: consent?.accepted ? `Accepted · ${consent.required_version}` : 'Not yet captured',
                  tone: consent?.accepted ? 'ok' : 'warn',
                },
              ].map((c) => (
                <div key={c.label} className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-5" data-testid={`protection-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-3 ${c.tone === 'warn' ? 'bg-cn-accent/12 text-cn-accent' : 'bg-cn-success/12 text-cn-success'}`}>
                    <c.icon className="w-5 h-5" />
                  </div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{c.label}</p>
                  <p className="text-sm font-semibold text-cn-text dark:text-white mt-1">{c.value}</p>
                  {c.sub && <p className="text-xs text-cn-text-muted mt-1">{c.sub}</p>}
                  {c.badge && (
                    <Badge className={`mt-2 ${c.tone === 'warn' ? 'bg-cn-accent/12 text-cn-accent border border-cn-accent/25' : 'bg-cn-success/12 text-cn-success border border-cn-success/25'}`}>{c.badge}</Badge>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Two-factor authentication */}
          <section data-testid="privacy-2fa-section">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-cn-primary" />
              <h2 className="cn-display text-2xl text-cn-text dark:text-white">Account security</h2>
            </div>
            <TwoFactorAuth />
          </section>

          {/* Data we hold */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-cn-primary" />
              <h2 className="cn-display text-2xl text-cn-text dark:text-white">Data we hold about you</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="privacy-categories">
              {overview?.categories?.map((cat) => (
                <div key={cat.key} className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-5 flex flex-col" data-testid={`category-${cat.key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-display font-bold text-cn-text dark:text-white">{cat.label}</h4>
                        {cat.sensitive && <Badge className="bg-cn-accent/12 text-cn-accent border border-cn-accent/25 text-[10px]">sensitive</Badge>}
                      </div>
                      <p className="text-xs text-cn-text-muted mt-1">{cat.desc}</p>
                    </div>
                    <span className="font-display font-extrabold text-2xl text-cn-primary leading-none flex-shrink-0" data-testid={`count-${cat.key}`}>{cat.count}</span>
                  </div>
                  <div className="mt-auto pt-4">
                    {cat.deletable ? (
                      <Button
                        onClick={() => setConfirmCat(cat)}
                        disabled={cat.count === 0}
                        className="cn-btn cn-btn-ghost !py-1.5 text-xs text-cn-error w-full disabled:opacity-40"
                        data-testid={`delete-category-${cat.key}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> {cat.count === 0 ? 'Nothing to delete' : 'Delete this data'}
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1.5 text-[11px] text-cn-text-muted justify-center py-1.5">
                        <Lock className="w-3 h-3" /> Retained for compliance
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Access log */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-cn-primary" />
              <h2 className="cn-display text-2xl text-cn-text dark:text-white">Who's accessed your data</h2>
            </div>
            <div className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 overflow-hidden" data-testid="privacy-access-log">
              {accessLog.length === 0 ? (
                <div className="p-10 text-center text-sm text-cn-text-muted">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-cn-success" />
                  No one has accessed your data yet. You're in full control.
                </div>
              ) : (
                accessLog.map((e, i) => {
                  const Icon = actorIcon(e.actor_role);
                  return (
                    <div key={i} className="flex items-start gap-3 px-5 py-4 border-b border-cn-border/50 last:border-0" data-testid={`access-entry-${i}`}>
                      <div className={`w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0 ${e.actor_role === 'system' ? 'bg-cn-secondary/15 text-cn-secondary' : 'bg-cn-primary/10 text-cn-primary'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-cn-text dark:text-white">{e.actor} <span className="text-cn-text-muted font-normal">· {e.actor_role}</span></p>
                        <p className="text-xs text-cn-text-muted mt-0.5">{e.action}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <Badge variant="outline" className="border-cn-border text-[10px]">{e.resource}</Badge>
                        {e.when && <p className="text-[10px] text-cn-text-muted/70 mt-1 flex items-center gap-1 justify-end"><Clock className="w-3 h-3" />{new Date(e.when).toLocaleDateString()}</p>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {/* Danger zone */}
          <section>
            <div className="cn-card border border-cn-error/30 bg-cn-error/5 p-6" data-testid="privacy-danger-zone">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-cn-error/10 text-cn-error flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="cn-display text-lg text-cn-text dark:text-white">Delete my account</h3>
                  <p className="text-sm text-cn-text-muted mt-1">
                    Permanently erase your account and all associated data. Financial and clinical records required by law are retained in de-identified form. This cannot be undone.
                  </p>
                  <Button onClick={() => { setConfirmText(''); setAccountDialog(true); }} className="cn-btn !bg-cn-error !text-white hover:!opacity-90 mt-4" data-testid="open-delete-account">
                    <Trash2 className="w-4 h-4" /> Delete my account
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Per-category delete confirm */}
      <AlertDialog open={!!confirmCat} onOpenChange={(v) => !v && setConfirmCat(null)}>
        <AlertDialogContent data-testid="category-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmCat?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {confirmCat?.count} {confirmCat?.label?.toLowerCase()} record{confirmCat?.count !== 1 ? 's' : ''} from CaraLoom. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="category-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(ev) => { ev.preventDefault(); deleteCategory(confirmCat); }}
              className="!bg-cn-error !text-white"
              data-testid="category-confirm"
            >
              {deletingCat === confirmCat?.key ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Account delete confirm */}
      <AlertDialog open={accountDialog} onOpenChange={(v) => !v && setAccountDialog(false)}>
        <AlertDialogContent data-testid="account-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              All of your personal data will be erased and you'll be signed out immediately. To confirm, type <span className="font-bold text-cn-error">DELETE</span> below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            className="h-11 rounded-xl"
            data-testid="account-confirm-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="account-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(ev) => { ev.preventDefault(); deleteAccount(); }}
              disabled={confirmText.trim().toUpperCase() !== 'DELETE' || deletingAccount}
              className="!bg-cn-error !text-white disabled:opacity-40"
              data-testid="account-confirm"
            >
              {deletingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
